/**
 * CardZoomController - Controlador genérico de zoom para tarjetas
 * 
 * Funciona con cualquier tipo de tarjeta (tickets, clientes, etc.)
 * Características:
 * - Zoom a pantalla completa en móvil
 * - Highlight con ring en desktop
 * - Edición inline opcional (si hay campos editables)
 * - Click fuera para cerrar
 * 
 * Uso:
 *   data-controller="card-zoom"
 *   data-card-zoom-target="card"
 *   data-card-zoom-card-id-value="123"           (ID del recurso)
 *   data-card-zoom-update-url-value="/path/:id"  (URL para PATCH, opcional)
 *   data-card-zoom-resource-name-value="ticket"  (nombre del recurso para params, opcional)
 *   data-action="click->card-zoom#toggle input->card-zoom#handleInput ..."
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "actions", "editableField", "closeButton", "fieldsContainer"]
  static values = { 
    cardId: Number,           // ID genérico del recurso
    updateUrl: String,        // URL para actualizaciones PATCH (opcional)
    resourceName: String,     // Nombre del recurso para params: ticket, customer, etc.
    detailUrl: String         // URL de detalle para navegación (opcional)
  }

  initialize() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.outsideListenerActive = false
  }

  connect() {
    this.activeCard = null
    
    // Listen for edit requests (e.g., from map markers for address field)
    this.handleEditRequest = this.handleEditRequest.bind(this)
    window.addEventListener('edit-card-address', this.handleEditRequest)
    // Mantener compatibilidad con tickets
    window.addEventListener('edit-ticket-address', this.handleEditRequest)
  }
  
  disconnect() {
    this.closeActiveCard()
    window.removeEventListener('edit-card-address', this.handleEditRequest)
    window.removeEventListener('edit-ticket-address', this.handleEditRequest)
  }
  
  // Handle edit request from external sources (e.g., map markers, warnings panel)
  handleEditRequest(event) {
    const eventId = event.detail.ticketId || event.detail.cardId
    if (eventId !== this.cardIdValue) return

    const fieldName = event.detail.field || 'direccion_reparacion'
    
    // First, zoom the card if not already zoomed
    if (!this.element.classList.contains('zoomed')) {
      this.toggle({ currentTarget: this.element, stopPropagation: () => {}, target: this.element })
    }

    // Small delay to ensure zoom animation completes
    setTimeout(() => {
      // Try to find existing field
      let field = this.editableFieldTargets.find(f => f.dataset.field === fieldName)
      
      // If field doesn't exist, create it dynamically
      if (!field) {
        field = this.createEmptyField(fieldName)
      }
      
      if (field) {
        field.removeAttribute('readonly')
        field.focus()
        field.select()
        // Highlight the field to draw attention
        field.classList.add('ring-2', 'ring-blue-400', 'bg-blue-50')
        setTimeout(() => {
          field.classList.remove('ring-2', 'ring-blue-400', 'bg-blue-50')
        }, 2000)
      }
    }, 100)
  }

  // Create an empty editable field dynamically when it doesn't exist
  createEmptyField(fieldName) {
    // Find the container for contact/location info using the target
    const infoContainer = this.hasFieldsContainerTarget 
      ? this.fieldsContainerTarget 
      : this.element.querySelector('.text-sm.text-gray-600.space-y-1')
    
    if (!infoContainer) {
      console.warn('CardZoom: Could not find fields container')
      return null
    }

    // Field configuration
    const fieldConfig = {
      nombre: { icon: '👤', placeholder: 'Añadir nombre...' },
      apellido: { icon: '👤', placeholder: 'Añadir apellido...' },
      telefono: { icon: '📞', placeholder: 'Añadir teléfono...' },
      email: { icon: '📧', placeholder: 'Añadir email...' },
      direccion_reparacion: { icon: '📍', placeholder: 'Añadir dirección...' },
      numero_bici: { icon: '🚲', label: 'Bici nº', placeholder: 'Nº' }
    }

    const config = fieldConfig[fieldName]
    if (!config) return null

    // Create the field wrapper
    const wrapper = document.createElement('div')
    wrapper.className = 'flex items-center gap-1 w-full md:max-w-[280px] min-w-0'
    wrapper.dataset.dynamicField = fieldName

    // Create icon span
    const iconSpan = document.createElement('span')
    iconSpan.className = 'flex-shrink-0 px-1 py-0.5'
    iconSpan.textContent = config.icon
    wrapper.appendChild(iconSpan)

    // Add label if exists
    if (config.label) {
      const labelSpan = document.createElement('span')
      labelSpan.className = 'text-sm px-1 py-0.5'
      labelSpan.textContent = config.label
      wrapper.appendChild(labelSpan)
    }

    // Create input field
    const input = document.createElement('input')
    input.type = 'text'
    input.dataset.cardZoomTarget = 'editableField'
    input.dataset.field = fieldName
    input.dataset.originalValue = ''
    input.className = 'editable-field bg-transparent border-2 border-blue-300 px-1 py-0.5 rounded transition-all text-sm pointer-events-auto cursor-text hover:bg-gray-50 focus:outline-none focus:border-blue-500 focus:bg-white min-w-0 flex-1'
    input.placeholder = config.placeholder
    input.size = 20
    wrapper.appendChild(input)

    // Insert at appropriate position in container
    infoContainer.appendChild(wrapper)

    // Register the new field target
    this.editableFieldTargets.push(input)

    return input
  }

  // ===== INLINE EDITING WITH EVENT DELEGATION =====
  
  // Handle input event - auto-resize fields
  handleInput(event) {
    const field = event.target
    if (!field.classList.contains('editable-field')) return
    if (!this.element.classList.contains('zoomed')) return
    
    if (field.tagName === 'INPUT') {
      const charCount = Math.max(field.value.length, 1)
      field.size = charCount
    } else if (field.tagName === 'TEXTAREA') {
      field.style.height = 'auto'
      field.style.height = field.scrollHeight + 'px'
    }
  }
  
  // Handle blur - submit if changed
  async handleBlur(event) {
    const field = event.target
    if (!field.classList.contains('editable-field')) return
    if (!this.element.classList.contains('zoomed')) return
    
    await this.submitField(field)
  }
  
  // Handle keydown - Enter to submit, Escape to cancel
  handleKeydown(event) {
    const field = event.target
    if (!field.classList.contains('editable-field')) return
    if (!this.element.classList.contains('zoomed')) return
    
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      field.blur()
    } else if (event.key === 'Escape') {
      field.value = field.dataset.originalValue || ''
      field.blur()
    }
  }
  
  // Submit field change to server
  async submitField(field) {
    // Solo si hay URL de actualización configurada
    if (!this.hasUpdateUrlValue || !this.updateUrlValue) return
    
    const fieldName = field.dataset.field
    const originalValue = field.dataset.originalValue || ''
    const newValue = field.value.trim()
    
    if (newValue === originalValue) return
    
    field.classList.add('opacity-50')
    
    try {
      const resourceName = this.resourceNameValue || 'record'
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          [resourceName]: {
            [fieldName]: newValue
          }
        })
      })

      if (response.ok) {
        field.dataset.originalValue = newValue
        field.classList.remove('opacity-50')
        field.classList.add('ring-2', 'ring-green-400')
        setTimeout(() => {
          field.classList.remove('ring-2', 'ring-green-400')
        }, 500)
        
        // Dispatch event for address updates
        if (fieldName === 'direccion_reparacion') {
          window.dispatchEvent(new CustomEvent('address-updated', { 
            detail: { cardId: this.cardIdValue, ticketId: this.cardIdValue } 
          }))
        }
        
      } else {
        field.value = originalValue
        field.classList.remove('opacity-50')
        field.classList.add('ring-2', 'ring-red-400')
        setTimeout(() => {
          field.classList.remove('ring-2', 'ring-red-400')
        }, 1000)
      }
    } catch (error) {
      console.error('Error updating field:', error)
      field.value = originalValue
      field.classList.remove('opacity-50')
      field.classList.add('ring-2', 'ring-red-400')
      setTimeout(() => {
        field.classList.remove('ring-2', 'ring-red-400')
      }, 1000)
    }
  }

  // ===== ZOOM FUNCTIONALITY =====

  toggle(event) {
    const newCard = event.currentTarget
    if (!newCard) return

    // Ignorar clicks que vienen de controllers específicos (resolución, etc.)
    if (event.target.closest('[data-controller*="ticket-resolution"]')) {
      return
    }

    event.stopPropagation()

    // Si hay una tarjeta activa diferente, desactivarla primero
    if (this.activeCard && this.activeCard !== newCard) {
      this.hideActions(this.activeCard)
      this.resetCard(this.activeCard)
    }

    // Limpiar cualquier otra tarjeta zoomed
    document.querySelectorAll(".zoomed").forEach((card) => {
      if (card !== newCard) {
        this.hideActions(card)
        this.resetCard(card)
      }
    })

    this.removeOutsideClickListener()

    const isMobile = window.innerWidth < 768
    
    if (isMobile) {
      const headerHeight = 45
      
      newCard.style.position = 'fixed'
      newCard.style.top = `${headerHeight}px`
      newCard.style.left = '0'
      newCard.style.width = '100vw'
      newCard.style.height = `calc(100vh - ${headerHeight}px)`
      newCard.style.zIndex = "9999"
      newCard.style.margin = '0'
      newCard.style.borderRadius = '0'
      newCard.style.overflowY = 'auto'
      newCard.style.overflowX = 'hidden'
      newCard.style.padding = '1.5rem 1rem'
      newCard.style.display = 'flex'
      newCard.style.flexDirection = 'column'
      newCard.style.justifyContent = 'center'
      
      document.body.style.overflow = 'hidden'
      this.addCloseButton(newCard)
      newCard.classList.add('mobile-zoom-active', 'shadow-2xl', 'zoomed')
    } else {
      newCard.style.zIndex = "50"
      newCard.classList.add("ring-4", "ring-orange-400", "shadow-2xl", "zoomed")
    }
    
    newCard.classList.remove("cursor-pointer", "hover:shadow-lg", "hover:scale-[1.02]")

    // Mostrar acciones si existen
    const actions = newCard.querySelector("[data-card-zoom-target='actions']")
    if (actions) {
      actions.classList.remove("hidden")

      // Cargar notas si existe el controller ticket-notas
      const notasController = this.application.getControllerForElementAndIdentifier(actions, "ticket-notas")
      if (notasController && typeof notasController.load === "function") {
        notasController.load()
      }
    }

    this.activeCard = newCard
    this.addOutsideClickListener()
    this.enableEditing(newCard)
  }
  
  // Método close para usar desde botones
  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.closeActiveCard()
  }
  
  enableEditing(card) {
    const fields = card.querySelectorAll('.editable-field')
    fields.forEach(field => {
      field.removeAttribute('readonly')
      if (field.tagName === 'TEXTAREA') {
        requestAnimationFrame(() => {
          field.style.height = 'auto'
          field.style.height = field.scrollHeight + 'px'
        })
      }
    })
  }
  
  disableEditing(card) {
    const fields = card.querySelectorAll('.editable-field')
    fields.forEach(field => {
      field.setAttribute('readonly', '')
      if (field.tagName === 'TEXTAREA') {
        field.style.height = ''
      }
    })
  }

  addCloseButton(card) {
    // Intentar usar botón global de la barra superior (móvil)
    const closeBtn = document.getElementById('mobile-zoom-close-btn')
    if (closeBtn) {
      closeBtn.classList.remove('hidden')
      const newCloseBtn = closeBtn.cloneNode(true)
      closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn)
      newCloseBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.closeActiveCard()
      })
    }
    
    // También mostrar closeButton target si existe (para tarjetas con botón propio)
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.remove('hidden')
    }
  }

  removeCloseButton(card) {
    const closeBtn = document.getElementById('mobile-zoom-close-btn')
    if (closeBtn) {
      closeBtn.classList.add('hidden')
    }
    
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.add('hidden')
    }
  }

  resetCard(card) {
    this.disableEditing(card)
    
    const isMobile = window.innerWidth < 768
    
    if (isMobile) {
      card.style.position = ''
      card.style.top = ''
      card.style.left = ''
      card.style.width = ''
      card.style.height = ''
      card.style.margin = ''
      card.style.borderRadius = ''
      card.style.overflowY = ''
      card.style.overflowX = ''
      card.style.padding = ''
      card.style.display = ''
      card.style.flexDirection = ''
      card.style.justifyContent = ''
      
      document.body.style.overflow = ''
      this.removeCloseButton(card)
      card.classList.remove('mobile-zoom-active')
    }
    
    card.style.zIndex = "1"
    card.classList.remove("ring-4", "ring-orange-400", "shadow-2xl", "zoomed")
    card.classList.remove("pointer-events-none")
    card.classList.add("cursor-pointer", "hover:shadow-lg", "hover:scale-[1.02]")
  }

  hideActions(card) {
    const actions = card?.querySelector("[data-card-zoom-target='actions']")
    if (actions && !actions.classList.contains("hidden")) {
      actions.classList.add("hidden")
    }
  }

  handleOutsideClick(event) {
    if (!this.activeCard) return
    if (this.activeCard.contains(event.target)) return
    this.closeActiveCard()
  }

  addOutsideClickListener() {
    if (this.outsideListenerActive) return
    document.addEventListener("click", this.handleOutsideClick, true)
    this.outsideListenerActive = true
  }

  removeOutsideClickListener() {
    if (!this.outsideListenerActive) return
    document.removeEventListener("click", this.handleOutsideClick, true)
    this.outsideListenerActive = false
  }

  closeActiveCard() {
    if (!this.activeCard) return
    this.hideActions(this.activeCard)
    this.resetCard(this.activeCard)
    this.activeCard = null
    this.removeOutsideClickListener()
  }
}
